import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import Card from 'components/card/Card';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import { DateTime } from 'luxon';
import { Fragment, useState } from 'react';
import { MdAttachMoney, MdCallSplit, MdPercent, MdShoppingCart } from 'react-icons/md';
import { getConsolidatedReport } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import { CUTOFF_HOUR_OPTIONS } from './cutoffHour';
import { MethodKey, MethodMap } from './types';

type GroupBy = 'day' | 'month';

interface ConsolidatedFilter {
  initialDate: string;
  finalDate: string;
  groupBy: GroupBy;
  // Hora de virada do dia operacional ('1'..'23'); ausente = corte à meia-noite.
  cutoffHour?: string;
}

// Ordem/labels/cores das 5 colunas de forma de pagamento (espelha paymentBucket
// do backend). "Crédito" usa yellow.* — o tema Chakra não tem token "amber".
const METHOD_ORDER: MethodKey[] = ['CASH', 'PIX', 'DEBIT', 'CREDIT', 'VOUCHER'];

const METHOD_LABEL: Record<MethodKey, string> = {
  CASH: 'Dinheiro',
  PIX: 'PIX',
  DEBIT: 'Débito',
  CREDIT: 'Crédito',
  VOUCHER: 'Voucher',
};

const METHOD_COLOR: Record<MethodKey, string> = {
  CASH: 'green',
  PIX: 'teal',
  DEBIT: 'orange',
  CREDIT: 'yellow',
  VOUCHER: 'pink',
};

const EMPTY_METHOD_MAP: MethodMap = {
  CASH: { bruto: 0, liquido: 0 },
  PIX: { bruto: 0, liquido: 0 },
  DEBIT: { bruto: 0, liquido: 0 },
  CREDIT: { bruto: 0, liquido: 0 },
  VOUCHER: { bruto: 0, liquido: 0 },
};

// Início do mês corrente até o fim de hoje — recorte padrão do relatório.
function defaultRange() {
  return {
    initialDate: DateTime.local().startOf('month').toISO(),
    finalDate: DateTime.local().endOf('day').toISO(),
  };
}

// Atalhos de janela. "Noite" existe porque casa noturna fecha de madrugada: o
// movimento de uma noite vai das 18:00 de ontem às 05:00 de hoje, e montar isso
// à mão nos dois datetime-local é o passo que todo mundo erra.
const PRESETS: { label: string; range: () => { initialDate: string; finalDate: string } }[] = [
  {
    label: 'Hoje',
    range: () => ({
      initialDate: DateTime.local().startOf('day').toISO(),
      finalDate: DateTime.local().endOf('day').toISO(),
    }),
  },
  {
    label: 'Ontem',
    range: () => ({
      initialDate: DateTime.local().minus({ days: 1 }).startOf('day').toISO(),
      finalDate: DateTime.local().minus({ days: 1 }).endOf('day').toISO(),
    }),
  },
  {
    label: 'Noite (18:00 → 05:00)',
    range: () => ({
      initialDate: DateTime.local()
        .minus({ days: 1 })
        .set({ hour: 18, minute: 0 })
        .startOf('minute')
        .toISO(),
      finalDate: DateTime.local().set({ hour: 5, minute: 0 }).endOf('minute').toISO(),
    }),
  },
];

export default function ConsolidatedReport() {
  const [draft, setDraft] = useState(defaultRange);
  const [filter, setFilter] = useState<ConsolidatedFilter>(() => ({ ...defaultRange(), groupBy: 'day' }));

  const { data, isLoading } = useQuery(
    ['reports', 'consolidated-customer', filter],
    () => getConsolidatedReport(filter),
    { enabled: !!filter.initialDate && !!filter.finalDate },
  );

  const onChangeInitialDate = (value: string) => {
    const parsed = DateTime.fromISO(value);
    if (!value || !parsed.isValid) return;
    setDraft((prev) => ({ ...prev, initialDate: parsed.toISO() }));
  };

  const onChangeFinalDate = (value: string) => {
    const parsed = DateTime.fromISO(value);
    if (!value || !parsed.isValid) return;
    setDraft((prev) => ({ ...prev, finalDate: parsed.toISO() }));
  };

  const buscar = () => {
    setFilter((prev) => ({ ...prev, initialDate: draft.initialDate, finalDate: draft.finalDate }));
  };

  // Preenche os dois campos e já busca — o preset só vale como atalho se for um
  // clique só.
  const aplicarPreset = (preset: (typeof PRESETS)[number]) => {
    const range = preset.range();
    setDraft(range);
    setFilter((prev) => ({ ...prev, ...range }));
  };

  const changeCutoffHour = (cutoffHour: string) => {
    setFilter((prev) => ({ ...prev, cutoffHour: cutoffHour || undefined }));
  };

  const changeGroupBy = (groupBy: GroupBy) => {
    setFilter((prev) => (prev.groupBy === groupBy ? prev : { ...prev, groupBy }));
  };

  const brandColor = useColorModeValue('brandText.900', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  const rows = data?.rows ?? [];
  const summary = data?.summary ?? {
    byMethod: EMPTY_METHOD_MAP,
    compras: 0,
    split: 0,
    taxa: 0,
    cliente: 0,
    estornos: 0,
  };
  const hasRows = rows.length > 0;

  return (
    <Box>
      {/* O título sai do navbar (`brandText`, da rota); repeti-lo aqui produzia duas vezes
          "Extrato Consolidado" uma sobre a outra. */}
      <Flex gap="8px" mb="12px" wrap="wrap">
        {PRESETS.map((preset) => (
          <Button
            key={preset.label}
            size="xs"
            variant="outline"
            colorScheme="brand"
            onClick={() => aplicarPreset(preset)}
          >
            {preset.label}
          </Button>
        ))}
      </Flex>

      <Flex
        direction={{ base: 'column', md: 'row' }}
        wrap="wrap"
        gap="16px"
        align={{ base: 'stretch', md: 'flex-end' }}
        mb="20px"
      >
        <FormControl w={{ base: '100%', md: '220px' }}>
          <FormLabel fontWeight="bold" fontSize="12" color="gray.500" mb="6px">
            DATA INICIAL
          </FormLabel>
          <Input
            type="datetime-local"
            size="sm"
            bg="white"
            value={draft.initialDate.substring(0, 16)}
            onChange={(e) => onChangeInitialDate(e.target.value)}
          />
        </FormControl>
        <FormControl w={{ base: '100%', md: '220px' }}>
          <FormLabel fontWeight="bold" fontSize="12" color="gray.500" mb="6px">
            DATA FINAL
          </FormLabel>
          <Input
            type="datetime-local"
            size="sm"
            bg="white"
            value={draft.finalDate.substring(0, 16)}
            onChange={(e) => onChangeFinalDate(e.target.value)}
          />
        </FormControl>
        <FormControl w={{ base: '100%', md: '180px' }}>
          <FormLabel fontWeight="bold" fontSize="12" color="gray.500" mb="6px">
            HORA DE VIRADA
          </FormLabel>
          <Select
            size="sm"
            bg="white"
            value={filter.cutoffHour ?? ''}
            onChange={(e) => changeCutoffHour(e.target.value)}
          >
            {CUTOFF_HOUR_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <Button colorScheme="brand" size="sm" onClick={buscar} flexShrink={0}>
          Buscar
        </Button>
        <ButtonGroup isAttached size="sm" variant="outline" ml={{ base: 0, md: 'auto' }}>
          <Button isActive={filter.groupBy === 'day'} onClick={() => changeGroupBy('day')}>
            Por dia
          </Button>
          <Button isActive={filter.groupBy === 'month'} onClick={() => changeGroupBy('month')}>
            Por mês
          </Button>
        </ButtonGroup>
      </Flex>

      {filter.cutoffHour ? (
        <Text fontSize="12" color="gray.500" mt="-12px" mb="20px">
          O dia fecha às {filter.cutoffHour.padStart(2, '0')}:00 — vendas antes desse horário entram
          no dia anterior.
        </Text>
      ) : null}

      {isLoading ? (
        <>
          <SimpleGrid columns={{ base: 1, md: 4 }} gap="20px" mb="20px">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="100px" borderRadius="20px" />
            ))}
          </SimpleGrid>
          <SimpleGrid columns={{ base: 2, md: 5 }} gap="16px" mb="20px">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height="90px" borderRadius="16px" />
            ))}
          </SimpleGrid>
          <Skeleton height="240px" borderRadius="20px" />
        </>
      ) : !hasRows ? (
        <Card>
          <Text color="secondaryGray.600">Sem vendas no período.</Text>
        </Card>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 4 }} gap="20px" mb="20px">
            <MiniStatistics
              startContent={
                <IconBox
                  w="56px"
                  h="56px"
                  bg={boxBg}
                  icon={<Icon w="28px" h="28px" as={MdShoppingCart} color={brandColor} />}
                />
              }
              name="Compras"
              value={maskCurrency(summary.compras)}
            />
            <MiniStatistics
              startContent={
                <IconBox
                  w="56px"
                  h="56px"
                  bg={boxBg}
                  icon={<Icon w="28px" h="28px" as={MdAttachMoney} color={brandColor} />}
                />
              }
              name="Cliente"
              value={maskCurrency(summary.cliente)}
            />
            <MiniStatistics
              startContent={
                <IconBox
                  w="56px"
                  h="56px"
                  bg={boxBg}
                  icon={<Icon w="28px" h="28px" as={MdPercent} color={brandColor} />}
                />
              }
              name="Taxa"
              value={maskCurrency(summary.taxa)}
            />
            <MiniStatistics
              startContent={
                <IconBox
                  w="56px"
                  h="56px"
                  bg={boxBg}
                  icon={<Icon w="28px" h="28px" as={MdCallSplit} color={brandColor} />}
                />
              }
              name="Split"
              value={maskCurrency(summary.split)}
            />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 2, md: 5 }} gap="16px" mb="20px">
            {METHOD_ORDER.map((method) => {
              const values = summary.byMethod[method];
              const color = METHOD_COLOR[method];
              return (
                <Card key={method} p="16px" borderTop="4px solid" borderColor={`${color}.400`}>
                  <Text fontSize="xs" fontWeight="700" color="secondaryGray.600" textTransform="uppercase" mb="8px">
                    {METHOD_LABEL[method]}
                  </Text>
                  <Text fontSize="xs" color="secondaryGray.600">
                    Bruto
                  </Text>
                  <Text fontSize="md" fontWeight="700" mb="6px" textAlign="right">
                    {maskCurrency(values.bruto)}
                  </Text>
                  <Text fontSize="xs" color="secondaryGray.600">
                    Líquido
                  </Text>
                  <Text fontSize="md" fontWeight="700" color={`${color}.500`} textAlign="right">
                    {maskCurrency(values.liquido)}
                  </Text>
                </Card>
              );
            })}
          </SimpleGrid>

          <Card p="0px" overflow="hidden">
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th rowSpan={2}>Data</Th>
                    {METHOD_ORDER.map((method) => (
                      <Th key={method} colSpan={2} textAlign="center">
                        {METHOD_LABEL[method]}
                      </Th>
                    ))}
                    <Th rowSpan={2} isNumeric>
                      Compras
                    </Th>
                    <Th rowSpan={2} isNumeric>
                      Split
                    </Th>
                    <Th rowSpan={2} isNumeric>
                      Taxa
                    </Th>
                    <Th rowSpan={2} isNumeric>
                      Cliente
                    </Th>
                  </Tr>
                  <Tr>
                    {METHOD_ORDER.map((method) => (
                      <Fragment key={method}>
                        <Th isNumeric fontSize="10px">
                          Bruto
                        </Th>
                        <Th isNumeric fontSize="10px">
                          Líquido
                        </Th>
                      </Fragment>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row, index) => (
                    <Tr key={row.groupId ?? `row-${index}`}>
                      <Td fontWeight="600" whiteSpace="nowrap">
                        {row.groupLabel}
                      </Td>
                      {METHOD_ORDER.map((method) => (
                        <Fragment key={method}>
                          <Td isNumeric>{maskCurrency(row.methods[method].bruto)}</Td>
                          <Td isNumeric>{maskCurrency(row.methods[method].liquido)}</Td>
                        </Fragment>
                      ))}
                      <Td isNumeric fontWeight="600">
                        {maskCurrency(row.totals.compras)}
                      </Td>
                      <Td isNumeric>{maskCurrency(row.totals.split)}</Td>
                      <Td isNumeric>{maskCurrency(row.totals.taxa)}</Td>
                      <Td isNumeric fontWeight="600">
                        {maskCurrency(row.totals.cliente)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Card>
        </>
      )}
    </Box>
  );
}
